import { useCallback } from "react"
import type LottieView from "lottie-react-native"
import type { AnimationObject } from "lottie-react-native"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"

interface UseLottieFrameManagerProps {
  totalSlides: number
  lottieRefs: React.RefObject<Array<LottieView | null>>
  slideWidth: number // Add slideWidth to the hook's props
}

export function useLottieFrameManager({
  totalSlides,
  lottieRefs,
  slideWidth,
}: UseLottieFrameManagerProps) {
  const { isWeb } = useDeviceAttributes()
  const goToFrame = useCallback((lottie: LottieView | null, frame: number) => {
    if (lottie) {
      // Jump directly to a frame and pause (play(frame, frame))
      lottie.play(frame, frame)
    }
  }, [])

  const onProgressChange = useCallback(
    (progress: number) => {
      if (!lottieRefs.current) {
        console.warn("Lottie refs are not initialized")
        return
      }

      // Normalize progress to the slide range [0, totalSlides - 1]
      const normalizedProgress = progress / slideWidth

      // Iterate through all slides
      lottieRefs.current.forEach((lottie, index) => {
        if (!lottie) return

        // Calculate the distance of this slide to the current progress
        const distance = Math.abs(index + normalizedProgress)
        const clampedDistance = Math.min(distance, 1) // Clamp between 0 and 1
        const slideProgress = 1 - clampedDistance // Opposite of distance for animation

        // Get the frame data for the current Lottie
        const getFrameData = (lottie: LottieView | null) => {
          if (!lottie || isWeb) return { op: 82 } // Use only the end frame and don't animate on web until 100% fixed
          const source = lottie.props.source as AnimationObject // Not recognized on Web
          return { op: source.op ?? 1 }
        }

        const { op } = getFrameData(lottie)
        const totalFrames = isWeb ? 83 : op - 1 // Calculate total frames (end frame - start frame, which is always 1), web has a bug

        // Interpolate frame based on slideProgress
        const currentFrame = Math.round(1 + slideProgress * totalFrames) - 1

        // Set the frame for the Lottie animation
        goToFrame(lottie, currentFrame)

        // Log details for debugging
        /* console.log(
          `Normalized: ${normalizedProgress.toFixed(2)}
       \tSlide: ${index}\tDistance: ${distance.toFixed(
         2
       )}\tSlide Progress: ${slideProgress.toFixed(
         2
       )}\tFrame: ${currentFrame}\tTotal Frames: ${totalFrames}`
        ) */
      })
      // console.log("") // Add a newline for better log separation
    },
    [lottieRefs, slideWidth, totalSlides]
  )

  return { onProgressChange }
}
